import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEffect, useState } from 'react';

export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'this_month'
  | 'last_month'
  | 'custom';

interface DateRangeFilterProps {
  value: { fromDate: string; toDate: string };
  onChange: (range: { fromDate: string; toDate: string }) => void;
  className?: string;
}

function calculateDateRange(preset: DateRangePreset): { fromDate: string; toDate: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  switch (preset) {
    case 'today': {
      return {
        fromDate: formatDate(today),
        toDate: formatDate(today),
      };
    }
    case 'yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return {
        fromDate: formatDate(yesterday),
        toDate: formatDate(yesterday),
      };
    }
    case 'this_week': {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      return {
        fromDate: formatDate(startOfWeek),
        toDate: formatDate(today),
      };
    }
    case 'this_month': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        fromDate: formatDate(startOfMonth),
        toDate: formatDate(today),
      };
    }
    case 'last_month': {
      const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        fromDate: formatDate(startOfLastMonth),
        toDate: formatDate(endOfLastMonth),
      };
    }
    case 'custom':
    default:
      return {
        // Fallback defaults to today
        fromDate: formatDate(today),
        toDate: formatDate(today),
      };
  }
}

export function DateRangeFilter({ value, onChange, className }: DateRangeFilterProps) {
  const [preset, setPreset] = useState<DateRangePreset>('today');
  const [isCustom, setIsCustom] = useState(false);

  useEffect(() => {
    // Initialize with today if dates are not provided
    if (!value.fromDate || !value.toDate) {
      const range = calculateDateRange('today');
      onChange(range);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePresetChange = (newPreset: DateRangePreset) => {
    setPreset(newPreset);
    if (newPreset === 'custom') {
      setIsCustom(true);
      // Keep current dates when switching to custom
    } else {
      setIsCustom(false);
      const range = calculateDateRange(newPreset);
      onChange(range);
    }
  };

  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fromDate = e.target.value;
    onChange({ fromDate, toDate: value.toDate });
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const toDate = e.target.value;
    onChange({ fromDate: value.fromDate, toDate });
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className || ''}`}>
      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-1 shadow-sm whitespace-nowrap shrink-0">
        <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">⏳ Period</span>
        <Select
          value={isCustom ? 'custom' : preset}
          onValueChange={(val) => handlePresetChange(val as DateRangePreset)}
        >
          <SelectTrigger className="w-[130px] sm:w-[140px] h-8 border-none shadow-none focus:ring-0">
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="yesterday">Yesterday</SelectItem>
            <SelectItem value="this_week">This Week</SelectItem>
            <SelectItem value="this_month">This Month</SelectItem>
            <SelectItem value="last_month">Last Month</SelectItem>
            <SelectItem value="custom">Custom Range</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isCustom && (
        <>
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-1 shadow-sm shrink-0">
            <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">From</span>
            <Input
              type="date"
              className="w-[130px] sm:w-auto sm:min-w-[140px] h-8 border-none shadow-none focus-visible:ring-0"
              value={value.fromDate}
              onChange={handleFromDateChange}
            />
          </div>
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-1 shadow-sm shrink-0">
            <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">To</span>
            <Input
              type="date"
              className="w-[130px] sm:w-auto sm:min-w-[140px] h-8 border-none shadow-none focus-visible:ring-0"
              value={value.toDate}
              onChange={handleToDateChange}
            />
          </div>
        </>
      )}
    </div>
  );
}
